import { BuyNow, List } from '@/components/buttons';
import { AddToCart } from '@/components/buttons/AddToCart';
import { FormatCryptoCurrency } from '@/components/primitives';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { UserToken } from '@/hooks/use-user-tokens';
import { useAuthentication } from '@/hooks/useAuthentication';
import { isImmutableChain } from '@/utils/immutable';
import { routes } from '@/utils/routes';
import {
  AspectRatio,
  Box,
  Flex,
  HStack,
  LinkOverlay,
  Stack,
  Tag,
  TagLabel,
  Text,
  Tooltip,
  useBreakpointValue,
  useTheme,
} from '@chakra-ui/react';
import { compareAddresses, formatAddress, formatNumber } from '@onbeam/utils';
import { TokenMedia, extractMediaType, type DynamicTokens } from '@sphere/reservoir-kit-ui';
import { Card, MotionBox, gradients, type CardProps } from '@sphere/ui';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { rem } from 'polished';
import { ReactNode, useCallback, useEffect, useState, type SyntheticEvent } from 'react';
import { useMeasure } from 'react-use';
import { toast } from 'sonner';
import { NftCardContextMenu } from './NftCardContextMenu';

type TokenCardProps = CardProps & {
  token: DynamicTokens[number] & { ownership?: UserToken['ownership'] };
  onMediaPlayed?: (e: SyntheticEvent<HTMLAudioElement | HTMLVideoElement, Event>) => void;
  showCollectionName?: boolean;
  buyEnabled?: boolean;
  listEnabled?: boolean;
  actionButton?: ReactNode;
  bottomLabel?: string;
  isUserOverview?: boolean;
  mutate?: () => void;
  quantityLabel?: {
    value: number;
    prefix?: string;
  };
};

export const NftCard = ({
  token,
  buyEnabled = true,
  listEnabled = true,
  onMediaPlayed,
  showCollectionName,
  bottomLabel,
  isUserOverview,
  mutate,
  quantityLabel,
  isActive,
  ...props
}: TokenCardProps) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const { userAddress, isAuthenticated } = useAuthentication();
  const marketplaceChain = useMarketplaceChain();
  const theme = useTheme();
  const [hovering, setHovering] = useState(false);
  const [contentRef, { height: contentHeight }] = useMeasure<HTMLDivElement>();
  const [assetRef, { width: assetWidth }] = useMeasure<HTMLDivElement>();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const isSelected = isActive || (token && token?.isInCart && isAuthenticated);
  const mediaType = extractMediaType(token?.token);
  const showMedia =
    mediaType === 'mp4' ||
    mediaType === 'mp3' ||
    mediaType === 'm4a' ||
    mediaType === 'wav' ||
    mediaType === 'mov';
  const { routePrefix } = useMarketplaceChain();
  const [isContextModalOpen, setContextModalOpen] = useState(false);
  const listModalState = useState(false);
  const buyModalState = useState(false);
  const [isCartModalOpen] = useState(false);

  const isOwner = compareAddresses(token?.token?.owner, userAddress) || isUserOverview;

  const floorAsk = token?.market?.floorAsk || token.ownership?.floorAsk;
  const isListMaker = compareAddresses(floorAsk?.maker, userAddress);
  const isERC1155 = token?.token?.kind === 'erc1155';

  const shouldRenderBuyButton = buyEnabled && !isListMaker && !!floorAsk?.price?.amount;
  const shouldRenderListButton =
    listEnabled &&
    isOwner &&
    !(isImmutableChain(marketplaceChain.id) && !isERC1155 && !!floorAsk?.id);
  const shouldRenderActionButton = shouldRenderBuyButton || shouldRenderListButton;

  useEffect(() => {
    if (listModalState[0] || buyModalState[0]) {
      setHovering(false);
    }
  }, [listModalState, buyModalState]);

  const onHover = useCallback(() => {
    if (!isContextModalOpen && !listModalState[0] && !buyModalState[0] && !isCartModalOpen) {
      setHovering(true);
    } else {
      setHovering(false);
    }
  }, [isContextModalOpen, listModalState, buyModalState, isCartModalOpen]);

  const onGoToToken = () =>
    router.push(
      routes.nft.detail(token?.token?.contract ?? '', token?.token?.tokenId ?? '', routePrefix),
    );

  return (
    <Card
      {...props}
      data-testid={`nft-card-${marketplaceChain.routePrefix}-${token.token?.contract}-${token.token?.tokenId}`}
      data-rarity={token.token?.rarityRank}
      display="flex"
      flexDirection="column"
      pb={0}
      bg="gray.2"
      role="group"
      onMouseEnter={onHover}
      onMouseLeave={() => setHovering(false)}
      onFocus={onHover}
      onBlur={() => setHovering(false)}
      // The height of the card is equal to the width (same as height) of the asset + the height of the content below
      {...(!props.backgroundAlwaysActive && {
        h: { md: contentHeight ? `${assetWidth + contentHeight}px` : '100%' },
      })}
      borderColor="gray.4"
      transition="all .2s ease"
      _hover={{ borderColor: 'gray.5' }}
      _focusWithin={{ borderColor: 'gray.5' }}
    >
      <Box h="100%" overflow="hidden" pos="relative">
        <Flex
          position="absolute"
          top="space.10"
          right="space.10"
          zIndex="in-front"
          gap="space.8"
          display={{ md: 'none' }}
          _groupHover={{ display: 'flex' }}
          _groupFocusWithin={{ display: 'flex' }}
        >
          {shouldRenderBuyButton && (
            <AddToCart
              token={token}
              variant="facet"
              h={rem(40)}
              quantityPickerProps={{ size: rem(40) }}
            />
          )}
          {isOwner && (
            <NftCardContextMenu
              token={token}
              onModalOpen={() => [setContextModalOpen(true), setHovering(false)]}
              onModalClose={() => setContextModalOpen(false)}
              onRefresh={mutate}
            />
          )}
        </Flex>

        <Box
          ref={assetRef}
          position="relative"
          minWidth={{ base: rem(96) }}
          overflow="hidden"
          borderTopRadius="radius.28"
        >
          <MotionBox
            overflow="hidden"
            borderTopRadius="radius.28"
            h="100%"
            animate={{
              margin: isSelected ? 10 : 0,
              height:
                hovering && shouldRenderActionButton && !isMobile
                  ? `${assetWidth - 56 - (isSelected ? 20 : 0)}px`
                  : 'auto',
            }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1.0] }}
          >
            {isSelected && (
              <AspectRatio pos="absolute" left={0} top={0} w="100%" ratio={1}>
                <Box borderTopRadius="inherit" bgGradient={gradients.selection} />
              </AspectRatio>
            )}
            <Link
              passHref
              href={routes.nft.detail(
                token.token?.contract ?? '',
                token.token?.tokenId ?? '',
                routePrefix,
              )}
            >
              <Box background="blackBg" overflow="hidden">
                <TokenMedia
                  token={token?.token}
                  style={{
                    transition: 'transform .2s ease-in-out',
                    maxHeight: 720,
                    width: '100%',
                    height: '100%',
                    borderRadius: 0,
                    aspectRatio: '1/1',
                    objectFit: 'contain',
                    backgroundColor: theme.colors.blackBg,
                  }}
                  hoverStyle={{
                    transform: shouldRenderActionButton ? 'scale(150%)' : 'scale(115%)',
                  }}
                  staticOnly={!showMedia}
                  imageResolution="medium"
                  // If enabled, contract reads will be performed which results in 404 calls when no wallet is connected
                  disableOnChainRendering={true}
                  audioOptions={{
                    onPlay: e => {
                      onMediaPlayed?.(e);
                    },
                  }}
                  videoOptions={{
                    onPlay: e => {
                      onMediaPlayed?.(e);
                    },
                  }}
                  onRefreshToken={() => {
                    toast.success(t('components.nft-card.refresh.title'), {
                      description: t('components.nft-card.refresh.description'),
                    });
                  }}
                />
              </Box>
            </Link>
          </MotionBox>
          {quantityLabel && quantityLabel.value > 1 && (
            <HStack position="absolute" bottom="space.10" left="space.10">
              <Tooltip
                label={formatNumber(quantityLabel.value, {
                  prefix: `${quantityLabel.prefix || ''} x`,
                  notation: 'standard',
                })}
                arrowSize={8}
                placement="top"
                hasArrow
                isDisabled={quantityLabel.value <= 1000}
              >
                <Tag data-testid="nft-card-tag" variant="dark" zIndex="in-front">
                  <TagLabel>
                    {formatNumber(quantityLabel.value, {
                      prefix: `${quantityLabel.prefix || ''} x`,
                    })}
                  </TagLabel>
                </Tag>
              </Tooltip>
            </HStack>
          )}
        </Box>
        <Box ref={contentRef}>
          <Stack
            p="space.16"
            pt="space.12"
            spacing="space.8"
            direction={{
              base: 'column',
              md: 'row',
            }}
            justifyContent={{
              base: 'space-between',
              md: 'space-between',
            }}
          >
            <Box lineHeight="shorter">
              <Link
                href={routes.nft.detail(
                  token.token?.contract ?? '',
                  token.token?.tokenId ?? '',
                  routePrefix,
                )}
                passHref
                legacyBehavior
              >
                <Text
                  fontSize="lg"
                  noOfLines={1}
                  as={LinkOverlay}
                  data-testid="nft-card-title"
                  _focus={{ outline: 'none' }}
                  _focusVisible={{ boxShadow: 'outline-inset' }}
                  wordBreak="break-all"
                >
                  {token?.token?.name || '#' + token?.token?.tokenId}
                </Text>
              </Link>
              {showCollectionName && token.token?.collection?.name && (
                <Text
                  mt="space.2"
                  fontSize="sm"
                  color="gray.6"
                  noOfLines={1}
                  data-testid="nft-card-collection-name"
                >
                  {formatAddress(token.token.collection.name)}
                </Text>
              )}
            </Box>
            {floorAsk?.price && (
              <Stack
                direction={{ base: 'row', md: 'column' }}
                spacing={0}
                alignItems="flex-end"
                justify={{ base: 'space-between', md: 'flex-start' }}
                lineHeight={1.3}
              >
                <HStack
                  spacing="space.4"
                  alignItems="center"
                  justifyContent="center"
                  data-testid="nft-card-price-container"
                >
                  <FormatCryptoCurrency
                    amount={floorAsk.price.amount?.decimal}
                    address={floorAsk.price.currency?.contract}
                    symbol={floorAsk.price.currency?.symbol}
                    logoHeight={16}
                    fontWeight="medium"
                    fontSize={{ base: 'sm', md: 'md' }}
                    maximumFractionDigits={3}
                  />
                </HStack>
              </Stack>
            )}
          </Stack>

          {bottomLabel && (
            <Text
              fontSize="xs"
              display={{ base: 'block', md: 'none' }}
              color="gray.4"
              noOfLines={1}
            >
              {bottomLabel}
            </Text>
          )}

          {shouldRenderActionButton && (
            <Flex
              pos={{ md: 'absolute' }}
              w="100%"
              px="space.12"
              opacity={{ md: 0 }}
              transition="opacity .2s"
              _groupHover={{
                opacity: 1,
              }}
              _groupFocusWithin={{ opacity: 1 }}
              mb={{ base: 'space.16', md: undefined }}
              gap="space.8"
            >
              {shouldRenderBuyButton && (
                <BuyNow
                  tokenId={token.token?.tokenId}
                  collectionId={token.token?.collection?.id}
                  buttonChildren={t('components.nft-card.actions.buy-now')}
                  h={rem(40)}
                  mutate={mutate}
                  openState={buyModalState}
                  sx={{
                    span: {
                      lineHeight: rem(30),
                    },
                  }}
                  onGoToToken={onGoToToken}
                  {...(shouldRenderListButton && { fontSize: 'sm' })}
                />
              )}
              {shouldRenderListButton && (
                <List
                  token={token}
                  variant={shouldRenderBuyButton ? 'secondary' : 'primary'}
                  w="full"
                  size="md"
                  h={rem(40)}
                  buttonChildren={t('components.nft-card.actions.list-for-sale')}
                  openState={listModalState}
                  onGoToToken={onGoToToken}
                  {...(shouldRenderBuyButton && { fontSize: 'sm' })}
                  data-testid="set-for-sale-button"
                />
              )}
            </Flex>
          )}
        </Box>
      </Box>
    </Card>
  );
};
